import { Modal, Popover, Space, Spin, Table } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CreditLimitAgingData } from '../../types';
import { getDataWithAuthToken } from '../../utils/axiosRequest';
import { alertMessage } from '../../utils/alertMessage';
import { setFont } from '../../utils/colComponents';

type CreditLimitDetailModalProps = {
  userId?: number;
  userName?: string;
  isSeller?: boolean;
  sellerId?: number;
  visible: boolean;
  setVisible: Dispatch<SetStateAction<boolean>>;
};

/**
 * 账期信用额度详情：按账龄分档（1-30 / 31-60 / 61-90 / 90+）展示未付金额
 * 用户详情页与账期信用额度统计列表共用
 */
const CreditLimitDetailModal = ({
  userId,
  userName,
  isSeller,
  sellerId,
  visible,
  setVisible,
}: CreditLimitDetailModalProps) => {
  const { t } = useTranslation();
  const isSubscribed = useRef(true);
  const [loading, setLoading] = useState(false);
  const [agingData, setAgingData] = useState<CreditLimitAgingData[]>([]);

  useEffect(() => {
    return () => {
      isSubscribed.current = false;
    };
  }, []);

  useEffect(() => {
    if (visible && userId) {
      setLoading(true);
      getDataWithAuthToken('users/credit_limit/detail', {
        params: { userId: userId, isSeller: isSeller, sellerId: sellerId },
      })
        .then((response) => {
          if (isSubscribed.current) {
            if (response && response.goodStatus) {
              setAgingData(response.data.list);
            } else {
              alertMessage(
                'error',
                response?.msg || t('general.noResponse'),
                response?.data || undefined
              );
            }
          }
        })
        .catch((err) => console.log(err))
        .finally(() => {
          if (isSubscribed.current) setLoading(false);
        });
    }
  }, [visible, userId, isSeller, sellerId, t]);

  return (
    <Modal
      visible={visible}
      title={`${t('analysis.userCreditLimit.detailTitle')}${
        userName ? ` - ${userName}` : ''
      }`}
      footer={null}
      onCancel={() => setVisible(false)}
    >
      <Spin spinning={loading}>
        <Table<CreditLimitAgingData>
          dataSource={agingData}
          rowKey={(row) => row.agingLabel}
          pagination={false}
          size="small"
          columns={[
            {
              title: (
                <Space size={4}>
                  {setFont(t('analysis.userCreditLimit.aging'))}
                  <Popover
                    overlayStyle={{ maxWidth: 320 }}
                    content={t('analysis.userCreditLimit.agingNote')}
                  >
                    <ExclamationCircleOutlined style={{ color: '#faad14' }} />
                  </Popover>
                </Space>
              ),
              key: 'agingLabel',
              dataIndex: 'agingLabel',
            },
            {
              title: setFont(t('analysis.userCreditLimit.totalUnpaidAmount')),
              key: 'totalUnpaidAmount',
              dataIndex: 'totalUnpaidAmount',
            },
            {
              title: setFont(t('analysis.userCreditLimit.unshippedAmount')),
              key: 'unshippedAmount',
              dataIndex: 'unshippedAmount',
            },
            {
              title: setFont(t('analysis.userCreditLimit.shippedAmount')),
              key: 'shippedAmount',
              dataIndex: 'shippedAmount',
            },
          ]}
        />
      </Spin>
    </Modal>
  );
};

export default CreditLimitDetailModal;
